import { useCallback, useEffect, useRef, useState } from 'react';
import { Form } from 'antd';
import { useMutation } from '@apollo/client';
import type { NamePath } from '@xbcb/ui-types';
import {
  ComplianceCheck,
  ComplianceCheckStatus,
  ComplianceCheckType,
  InvokeReasonabilityApiDocument,
  ReasonabilityOperation,
} from '__generated__/graphql';

export interface ReasonabilityDetails {
  /** AI rationale explaining the reasonability verdict. */
  rationale?: string;
  /** Confidence score for the evaluation, when the service returns one. */
  confidence?: number;
  /** True while the GetReasonabilityEvaluation request is in flight. */
  loading: boolean;
}

// The REASONABILITY compliance check stores its reference as `batchId#evaluationId`
// GetReasonabilityEvaluation only needs the evaluationId.
const parseEvaluationId = (reference?: string | null): string | undefined => {
  if (!reference) return undefined;
  const separatorIndex = reference.indexOf('#');
  return separatorIndex >= 0 ? reference.slice(separatorIndex + 1) : reference;
};

const toNumber = (value: unknown): number | undefined =>
  typeof value === 'number' && Number.isFinite(value) ? value : undefined;

interface UseReasonabilityDetailsParams {
  /** Line-item name path (e.g. invoices.X.products.Y.lines.Z). */
  linePath: NamePath;
  /** Latest REASONABILITY compliance-check status for the line. */
  status?: ComplianceCheckStatus | null;
  userId?: string;
}

// Fetches the rationale + confidence for a line's reasonability evaluation via
// invokeReasonabilityApi(GetReasonabilityEvaluation). Runs once the evaluation
// has a terminal verdict (COMPLETED/passed or FAILED) and a valid evaluationId
// is present, and fetches at most once per evaluationId.
export const useReasonabilityDetails = ({
  linePath,
  status,
  userId,
}: UseReasonabilityDetailsParams): ReasonabilityDetails => {
  const form = Form.useFormInstance();
  const complianceChecks = Form.useWatch(
    [...(linePath as (string | number)[]), 'complianceChecks'],
    form,
  ) as ComplianceCheck[] | undefined;

  const reference = complianceChecks?.find(
    (check) => check.type === ComplianceCheckType.REASONABILITY,
  )?.requestId;
  const evaluationId = parseEvaluationId(reference);

  const [invokeReasonability] = useMutation(InvokeReasonabilityApiDocument);
  const [details, setDetails] = useState<ReasonabilityDetails>({
    loading: false,
  });
  // Guards against refetching for the same evaluationId across re-renders.
  const fetchedForRef = useRef<string | undefined>();

  const fetchDetails = useCallback(
    async (id: string) => {
      // Set the guard before the awaited request (matching useAutoValidateHts /
      // the useHtsClassification mount effect) to prevent duplicate in-flight
      // fetches if the effect re-fires while this one is pending.
      fetchedForRef.current = id;
      setDetails((prev) => ({ ...prev, loading: true }));
      try {
        const { data } = await invokeReasonability({
          variables: {
            input: {
              apiName: ReasonabilityOperation.GetReasonabilityEvaluation,
              apiArgs: { evaluationId: id, userId, clientId: 'CBMSCustomsUI' },
            },
          },
        });
        const response = data?.invokeReasonabilityApi?.response;
        setDetails({
          rationale: response?.rationale ?? undefined,
          confidence:
            toNumber(response?.confidence) ??
            toNumber(response?.confidenceScore),
          loading: false,
        });
      } catch {
        // Leave details empty on failure; the card falls back to its default copy.
        setDetails({ loading: false });
      }
    },
    [invokeReasonability, userId],
  );

  useEffect(() => {
    const hasTerminalVerdict =
      status === ComplianceCheckStatus.COMPLETED ||
      status === ComplianceCheckStatus.FAILED;
    if (
      !hasTerminalVerdict ||
      !evaluationId ||
      fetchedForRef.current === evaluationId
    ) {
      return;
    }
    void fetchDetails(evaluationId);
  }, [status, evaluationId, fetchDetails]);

  return details;
};
